'use client';

import { Suspense, useEffect, useRef } from 'react';

import dynamic from 'next/dynamic';

import { config } from '@ps/config/client';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { getDeviceId } from '@ps/functions/utils/deviceId';
import { useCurrentUserId } from '@ps/ui/contexts/auth';

/**
 * Lazy client island for the bio welcome-video floating mini player. Loaded with
 * `ssr: false` so the player (video element + drag logic + the client welcome-
 * video fetch) stays out of the visitor first-load bundle and only downloads
 * after hydration. The mount self-hides when there's no public welcome video or
 * the visitor dismissed it (72h cooldown), so mounting it unconditionally on the
 * public bio is cheap.
 *
 * Only the public visitor bio renders this (this `(profile)` layout is never the
 * editor), which satisfies the legacy `!editable` visibility gate by placement.
 */
const BioWelcomeVideoMount = dynamic(
  () =>
    import('@ps/features/bio/welcome-video/BioWelcomeVideoMount').then(
      mod => mod.BioWelcomeVideoMount,
    ),
  { ssr: false },
);

/**
 * A LOCAL `<Suspense fallback={null}>` is REQUIRED here. This island is rendered
 * bare inside the `(profile)` layout, which sits under that layout's
 * `<Suspense fallback={<BioShellLoading/>}>`. Without a boundary of its own, the
 * `ssr: false` chunk's first-mount suspension bubbles UP to the route boundary
 * and swaps the WHOLE bio (including `ShellFrame`'s themed background) for the
 * backdrop-less loading shell — a full-page white flash before it repaints. The
 * local boundary (fallback `null`, since the floating player shows nothing until
 * its video resolves anyway) catches the suspension so it never reaches the
 * route. See [[reference_lazy_modal_suspense_bubbling]].
 */
export function WelcomeVideoIsland({ bioContext }: { bioContext: BioContext }) {
  const currentUserId = useCurrentUserId();
  const tracked = useRef(false);

  // Feeds the creator's Profile Views / New Visitors stats; owners are not counted.
  useEffect(() => {
    if (tracked.current || currentUserId === undefined) return;
    tracked.current = true;
    if (currentUserId === bioContext.creatorId) return;
    // Straight to our API, not Segment: only this path feeds the Doris / BigQuery view counts.
    const now = new Date().toISOString();
    // Signed-in visitors send their token so the server records their user_id.
    const token = currentUserId
      ? import('@ps/functions/firebase/auth').then(({ auth }) =>
          auth().currentUser?.getIdToken(),
        )
      : Promise.resolve(undefined);
    void token
      .then(idToken =>
        fetch(`${config.apiEndpoint}/v1/t`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken && { Authorization: `Bearer ${idToken}` }),
          },
          keepalive: true,
          body: JSON.stringify({
            type: 'track',
            event: 'popstore_bio_page_viewed',
            messageId: crypto.randomUUID(),
            anonymousId: getDeviceId(),
            timestamp: now,
            sentAt: now,
            properties: {
              element_action: 'popstore_bio_page_viewed',
              creator_id: bioContext.creatorId,
            },
          }),
        }),
      )
      .catch(() => {});
  }, [currentUserId, bioContext.creatorId]);

  return (
    <Suspense fallback={null}>
      <BioWelcomeVideoMount bioContext={bioContext} />
    </Suspense>
  );
}
